import { Box, Stack, Typography } from "@mui/material";
import { jobPreviewFacts, type JobPreviewFacts } from "@domain/CRM/job";
import { JobStatusChip } from "@gui/widgets/JobStatusChip";
import { MONO_FONT_FAMILY } from "@gui/providers/typography";
import type { JobLookupRow } from "./JobLookupSearch.vm";

/** The facts the card shows, in order. */
const FACTS: [string, keyof JobPreviewFacts][] = [
	["Season", "season"],
	["Acres", "acres"],
	["Depth", "depth"],
	["Lab", "lab"],
];

function FactGrid({ facts }: { facts: JobPreviewFacts }) {
	return (
		<Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
			{FACTS.map(([label, key]) => (
				<Box key={key} sx={{ minWidth: 0 }}>
					<Typography component="dt" variant="caption" color="text.secondary">
						{label}
					</Typography>
					<Typography component="dd" variant="body2" noWrap sx={{ m: 0 }}>
						{facts[key]}
					</Typography>
				</Box>
			))}
		</Box>
	);
}

/** The highlighted job, in the card beside the list, drawn from its row alone: the search returns all of this, and a
 *  card reads no job. Not a live region: arrowing down the list would read it out row by row, and the option in focus
 *  already says which job this is. */
export function JobLookupPane({ row }: { row: JobLookupRow }) {
	const facts = jobPreviewFacts(row);
	return (
		<Stack component="section" aria-label="Job details" spacing={1.25} sx={{ p: 2, minWidth: 0 }}>
			<Stack direction="row" spacing={1} sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
				<Typography variant="body1" sx={{ fontWeight: 500, minWidth: 0, overflowWrap: "anywhere" }}>
					{row.title}
				</Typography>
				{row.status && <JobStatusChip status={row.status} compact sx={{ flexShrink: 0 }} />}
			</Stack>
			<Box>
				{[facts.where, facts.company].filter(Boolean).map((line) => (
					<Typography key={line} variant="caption" component="div" color="text.secondary">
						{line}
					</Typography>
				))}
			</Box>
			<FactGrid facts={facts} />
			<Typography variant="micro" color="text.secondary" noWrap sx={{ fontFamily: MONO_FONT_FAMILY }}>
				{row.id}
			</Typography>
		</Stack>
	);
}
